import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { adminTutorialVideoApi } from '../../api/adminTutorialVideo.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import { resolveAssetUrl } from '../../utils/assetUrl';
import { copyToClipboard } from '../subscription-plans/planStripeStatus';
import '../subscription-plans/subscription-plans.css';
import './tutorial-videos.css';

const emptyForm = {
  title: '',
  slug: '',
  fileUrl: '',
  mediaType: '',
  fileName: '',
  mimeType: '',
  fileSize: null,
  isActive: true,
  displayOrder: 0,
};

const MEDIA_LABELS = {
  pdf: 'PDF',
  video: 'Video',
  document: 'Document',
};

const ACCEPT_TYPES = '.pdf,.doc,.docx,.txt,.rtf,.odt,.mp4,.webm,.mov,.m4v';

function slugifyTitle(title) {
  return String(title || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

export default function TutorialVideoEditor() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isNew = videoId === 'new';
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [copyMessage, setCopyMessage] = useState('');

  useEffect(() => {
    if (isNew || !videoId) return;
    const load = async () => {
      const result = await apiHandler(() => adminTutorialVideoApi.getById(videoId));
      if (result.success) {
        setForm({ ...emptyForm, ...unwrapData(result) });
      } else {
        setError(result.message || 'Failed to load tutorial video');
      }
      setLoading(false);
    };
    load();
  }, [videoId, isNew]);

  const handleTitleChange = (title) => {
    setForm((prev) => ({
      ...prev,
      title,
      slug: isNew ? slugifyTitle(title) : prev.slug,
    }));
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setError('');
    const result = await apiHandler(() => adminTutorialVideoApi.upload(file));
    if (result.success) {
      const uploaded = unwrapData(result);
      setForm((prev) => ({
        ...prev,
        fileUrl: uploaded.url,
        mediaType: uploaded.mediaType,
        fileName: uploaded.fileName,
        mimeType: uploaded.mimeType,
        fileSize: uploaded.fileSize,
      }));
    } else {
      setError(result.message || 'File upload failed');
    }
    setUploading(false);
  };

  const handleCopyLink = async () => {
    if (!form.fileUrl) return;
    const ok = await copyToClipboard(resolveAssetUrl(form.fileUrl));
    if (ok) {
      setCopyMessage('File link copied');
      setTimeout(() => setCopyMessage(''), 2500);
    } else {
      setError('Could not copy link');
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (isNew && !form.fileUrl) {
      setError('Please upload a PDF, video, or document file');
      return;
    }

    setSaving(true);
    setError('');

    const payload = isNew
      ? {
          title: form.title.trim(),
          slug: form.slug.trim(),
          fileUrl: form.fileUrl,
          mediaType: form.mediaType,
          fileName: form.fileName,
          mimeType: form.mimeType || undefined,
          fileSize: form.fileSize ?? undefined,
          displayOrder: Number(form.displayOrder || 0),
          isActive: Boolean(form.isActive),
        }
      : {
          title: form.title.trim(),
          displayOrder: Number(form.displayOrder || 0),
          isActive: Boolean(form.isActive),
          ...(form.fileUrl ? {
            fileUrl: form.fileUrl,
            mediaType: form.mediaType,
            fileName: form.fileName,
            mimeType: form.mimeType || undefined,
            fileSize: form.fileSize ?? undefined,
          } : {}),
        };

    const result = await apiHandler(() => (
      isNew
        ? adminTutorialVideoApi.create(payload)
        : adminTutorialVideoApi.update(videoId, payload)
    ));

    if (result.success) {
      navigate('/tutorial-videos');
    } else {
      setError(result.message || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="sp-page sp-loading">
        <i className="fa-solid fa-spinner fa-spin" /> Loading tutorial…
      </div>
    );
  }

  return (
    <div className="sp-page tv-page">
      <PageHeader
        title={isNew ? 'Add Tutorial Video' : `Edit ${form.title || 'Tutorial'}`}
        subtitle="Upload a PDF, video, or document with a title for application users"
        actions={(
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/tutorial-videos')}>
            <i className="fa-solid fa-arrow-left" /> All tutorials
          </button>
        )}
      />

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}
      {copyMessage && <div className="sp-alert sp-alert-success">{copyMessage}</div>}

      <div className="tv-form-card">
        <div className="tv-form-row">
          <label>Title</label>
          <input
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="How to Send an Alert"
          />
        </div>

        <div className="tv-form-row">
          <label>Slug</label>
          <input
            value={form.slug}
            readOnly
            placeholder="how_to_send_an_alert"
            className="tv-readonly"
          />
          <p className="tv-hint">
            {isNew
              ? 'Auto-generated from title when you create the tutorial.'
              : 'Slug cannot be changed after creation.'}
          </p>
        </div>

        <div className="tv-form-row">
          <label>File</label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_TYPES}
            className="tv-hidden-input"
            onChange={handleFileSelect}
          />
          <button
            type="button"
            className="sp-btn sp-btn-secondary tv-upload-btn"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className={`fa-solid ${uploading ? 'fa-spinner fa-spin' : 'fa-upload'}`} />
            {uploading ? 'Uploading…' : (form.fileName ? 'Replace file' : 'Upload file')}
          </button>
          <p className="tv-hint">Allowed: PDF, video (mp4/webm/mov), or document (doc/docx/txt/rtf/odt)</p>
          {form.fileName && (
            <div className="tv-file-info">
              <div>
                <strong>{form.fileName}</strong>
                {form.mediaType && (
                  <span className="tv-media-badge" style={{ marginLeft: 8 }}>
                    {MEDIA_LABELS[form.mediaType] || form.mediaType}
                  </span>
                )}
              </div>
              {form.fileUrl && (
                <div style={{ marginTop: 6, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <a href={resolveAssetUrl(form.fileUrl)} target="_blank" rel="noopener noreferrer">
                    Preview / download
                  </a>
                  <button
                    type="button"
                    className="sp-btn sp-btn-secondary sp-btn-sm"
                    onClick={handleCopyLink}
                  >
                    <i className="fa-solid fa-link" /> Copy link
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="tv-form-row">
          <label>Display order</label>
          <input
            type="number"
            value={form.displayOrder}
            onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
            min="0"
          />
        </div>

        {!isNew && (
          <div className="tv-form-row">
            <label className="sp-toggle">
              <input
                type="checkbox"
                checked={!!form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
        )}

        <div className="tv-footer-actions">
          <button type="button" className="sp-btn sp-btn-secondary" onClick={() => navigate('/tutorial-videos')}>
            Cancel
          </button>
          <button type="button" className="sp-btn sp-btn-primary" disabled={saving || uploading} onClick={save}>
            {saving ? 'Saving…' : 'Save tutorial'}
          </button>
        </div>
      </div>
    </div>
  );
}
